import IToken from '../ABI/IToken.json'
import IMA from '../ABI/IMA.json'
import MA_V2_ABI from './abi/ma-v2.json'
import RANK_V2_ABI from './abi/rank-v2.json'
import REWARD_CONTROLLER_ABI from './abi/reward-controller.json'
import CVCD_SWAP_ABI from './abi/cvcd-swap.json'
import {
    MA_V2,
    RANK_V2,
    REWARD_CONTROLLER,
    CVCD_SWAP,
    PROTOCOL_HASH,
    USDT,
    CVC,
    CVCD,
} from './network'
import {
    MA,
    MultiCallAddress
} from '../contract'
import {
    SECURITY_TREASURY
} from '../ma'
import {
    WeiToUnits,
    Erc20,
    MultiCall,
    GetGlobalSigner,
    CreateContract,
    Approves,
    BN,
    ZERO_ADDRESS,
} from '../../ethers/e'

const USDT_TOKEN = [USDT, 18, 'USDT', 'Tether (USDT)']
const CVC_TOKEN = [CVC, 18, 'CVC', 'Contribution value Coin']
const CVCD_TOKEN = [CVCD, 18, 'CVCD', 'USD CVC']
const V1_RESERVE_BASE = BN(10).pow(18).mul(100_000_000)

function Erc20Wrap(address) {
    return CreateContract(address, IToken, GetGlobalSigner())
}

function MAContract() {
    return CreateContract(MA_V2, MA_V2_ABI, GetGlobalSigner())
}

function V1MAContract() {
    return CreateContract(MA, IMA, GetGlobalSigner())
}

function RankContract() {
    return CreateContract(RANK_V2, RANK_V2_ABI, GetGlobalSigner())
}

function RewardController() {
    return CreateContract(REWARD_CONTROLLER, REWARD_CONTROLLER_ABI, GetGlobalSigner())
}

// CVCD_SWAP_JS_START: CvcdSwap contract helpers.
function CvcdSwapContract() {
    return CreateContract(CVCD_SWAP, CVCD_SWAP_ABI, GetGlobalSigner())
}
// CVCD_SWAP_JS_END: CvcdSwap contract helpers.

function Multi() {
    return MultiCall(MultiCallAddress, GetGlobalSigner())
}

export function Usdt() {
    return {
        token: USDT_TOKEN,
        contract: Erc20(USDT, GetGlobalSigner())
    }
}

export function Cvc() {
    return {
        token: CVC_TOKEN,
        contract: Erc20Wrap(CVC)
    }
}

export function Cvcd() {
    return {
        token: CVCD_TOKEN,
        contract: Erc20Wrap(CVCD)
    }
}

function identityFromNodeCost(nodeCost) {
    if (nodeCost.gte(BN(3000).mul(BN(10).pow(18)))) return 2
    if (nodeCost.gt(0)) return 1
    return 0
}

function toNumber(v) {
    if (v && typeof v.toNumber === 'function') return v.toNumber()
    return Number(v || 0)
}

function vipFromLevels(levels) {
    return Math.max(toNumber(levels.maxLevel), toNumber(levels.initLevel)) + 1
}

function normalizeReleaseList(releaseList) {
    let totalReleaseableCVC = BN(0)
    const list = releaseList.t.map((v, i) => {
        const id = v.toNumber()
        const bal = releaseList.cvcReleasable[i]
        totalReleaseableCVC = totalReleaseableCVC.add(bal)
        return {
            id,
            startAt: (new Date(id * 1000)).Format("yy/MM/dd hh:mm"),
            balance: WeiToUnits(bal)
        }
    }).sort((a, b) => b.id - a.id)

    return {
        releasedCVC: WeiToUnits(releaseList.releasedCVC),
        releaseList: list,
        totalReleaseableCVC: WeiToUnits(totalReleaseableCVC)
    }
}

function normalizeTree(teamRecords, account) {
    const root = account.toLowerCase()
    const directMap = {}
    const pendingChildren = {}
    let directPerformance = 0
    let level2Performance = 0

    teamRecords.forEach(v => {
        const user = v.user
        const sponsor = v.sponsor
        const sponsorKey = sponsor.toLowerCase()
        const invested = WeiToUnits(v.usdtInvest)
        const item = {
            user,
            vip: toNumber(v.vip) + 1,
            identity: toNumber(v.identity),
            invested,
            children: []
        }

        if (sponsorKey === root) {
            directMap[user.toLowerCase()] = item
            directPerformance += invested * 1
        } else {
            if (!pendingChildren[sponsorKey]) pendingChildren[sponsorKey] = []
            pendingChildren[sponsorKey].push(item)
            level2Performance += invested * 1
        }
    })

    Object.keys(pendingChildren).forEach(sponsor => {
        if (directMap[sponsor]) {
            directMap[sponsor].children = pendingChildren[sponsor]
        }
    })

    return {
        list: Object.keys(directMap).map(user => directMap[user]),
        directPerformance,
        level2Performance
    }
}

const NODE_TYPE = {
    usdtInvested: 0,
    releaseList: [],
    totalReleaseableCVC: 0,
    releasedCVC: 0,
    releaseCycle: '--',

    rankRewardedUSDT: 0,
    rankTopPending: 0,
    rankDisqualified: false,
    totalClaim: 0,
    shareReward: 0,
    sharePercent: 0,

    tree: [],
    nodeClaim: 0,
    level2Reward: 0,
    directReward: 0,
    teamReward: 0,
    directPerformance: 0,
    level2Performance: 0,

    vip: 0,
    identity: 0,
    selfLevel: 0,
    isNode: null,
    levels: '--',

    rankHeadId: '--',
    userRankId: '--',
    sponsor: ZERO_ADDRESS,
    claimCVC: 0,
    v1Sponsor: ZERO_ADDRESS,
    totalInvested: 0,
    securityTreasuryAddress: ZERO_ADDRESS,
    securityTreasuryUSDT: 0,
    rankHeadAddress: ZERO_ADDRESS,
    registed: null,
    feeReaward: 0,
    totalClaimed: 0,
    level2RewardTotal: 0,
    directRewardTotal: 0,
    teamRewardTotal: 0,
    feeReawardTotal: 0
}

export async function NodeData(account) {
    const ma = MAContract()
    const v1Ma = V1MAContract()
    const rank = RankContract()
    const reward = RewardController()
    const multiCall = Multi()

    // console.log(
    //     ma
    // )
    // let zeros = await ma.userOfsponsorList(account)
    // let zerosRe = await multiCall.all(
    //         zeros.map(v => reward.calls.getNodePendingRewarded(v))
    //     )

    // console.log(
    //     // zerosRe.map(v => v.fee.toNumber())
    //     zeros
    // )
    // zerosRe = zerosRe.map((v , i) => {
    //     return {
    //         ...v,
    //         address: zeros[i]
    //     }
    // })
    // zerosRe = zerosRe.filter(v => v.fee.toString() > 0)
    
    // console.log(
    //     zerosRe
    // )
// // 0x2eaCa84A2b8CF96C849BCe61a8aDf2F208B75Db9

    // console.log(
    //     "getTeamLeaderOf ", await ma.getTeamLeaderOf(account)
    // )

    // console.log(
    //     "funder ", await ma.funder()
    // )

    // console.log(ma)
    // console.log(
    //     await ma.getTeamLeaderOf(account)," 12312"
    // )
    // console.log('NodeData account:', account)
    let results
    try {
        const topRank = await rank.currentTop()
        const topRankUser = topRank.user || topRank[0]
        console.log('NodeData topRank:', topRank, ' topRankUser:', topRankUser, " account", account)

        // function getNodeTotalRewarded(address _user) public view returns (uint256 direct, uint256 indirect, uint256 team, uint256 fee)
        const callResults = await multiCall.all({
            sponsor: ma.calls.sponsor(account),
            userLevels: ma.calls.userLevels(account),
            nodeCost: reward.calls.getNodeCost(account),
            nodeTotal: reward.calls.getNodeTotalRewarded(account),
            nodePending: reward.calls.getNodePendingRewarded(account),
            
            rankPending: reward.calls.getRankPendingRewarded(account),
            nodeCostLedger: reward.calls.getTotalCostLedger(ZERO_ADDRESS),
            usdtCostLedger: reward.calls.getTotalCostLedger(USDT),
            cvcdCostLedger: reward.calls.getTotalCostLedger(CVCD),
            v1TotalInvested: v1Ma.calls.totalInvested(),
            v1TotalOrderInvested: v1Ma.calls.totalOrderInvested(),
            v1Sponsor: v1Ma.calls.sponsor(account),
            securityTreasury: reward.calls.securityTreasury(),
            rankTopPending: reward.calls.getRankRewarded(topRankUser),
            rankTopOf: rank.calls.rankOf(topRankUser),
            rankOf: rank.calls.rankOf(account),
            releaseList: reward.calls.releaseCVCList(account),
            tree: ma.calls.tree(account),
            claimCVC: reward.calls.claimCvc(account),
            levels: ma.calls.currentVipOf(account),
        })
        results = {
            sponsor: callResults.sponsor,
            userLevels: callResults.userLevels,
            nodeCost: callResults.nodeCost,
            nodePending: callResults.nodePending,
            nodeTotal: callResults.nodeTotal,
            rankPending: callResults.rankPending,
            nodeCostLedger: callResults.nodeCostLedger,
            usdtCostLedger: callResults.usdtCostLedger,
            cvcdCostLedger: callResults.cvcdCostLedger,
            v1TotalInvested: callResults.v1TotalInvested,
            v1TotalOrderInvested: callResults.v1TotalOrderInvested,
            securityTreasury: callResults.securityTreasury,
            rankTop: topRank,
            rankTopPending: callResults.rankTopPending,
            rankTopOf: callResults.rankTopOf,
            rankOf: callResults.rankOf,
            releaseList: callResults.releaseList,
            tree: callResults.tree,
            claimCVC: callResults.claimCVC,
            v1Sponsor: callResults.v1Sponsor,
            levels: callResults.levels,
            teamLeader: {
                _teamLeader: ZERO_ADDRESS,
                _topLeader: ZERO_ADDRESS
            },
        }
        console.log('NodeData callResults:', callResults)
    } catch (e) {
        console.log(e)
        return NODE_TYPE
    }

    if (results.sponsor !== ZERO_ADDRESS || results.nodeCost.gt(0)) {
        try {
            results.teamLeader = await ma.getTeamLeaderOf(account)
        } catch (e) {
            console.log('getTeamLeaderOf skipped:', e.reason || e.code || e.message)
        }
    }

    console.log('NodeData results:', results)
    let securityTreasuryUSDT = BN(0)
    try {
        const usdt = Erc20(USDT, GetGlobalSigner())
        const v2SecurityTreasuryUSDT = await usdt.balanceOf(results.securityTreasury)
        securityTreasuryUSDT = v2SecurityTreasuryUSDT
    } catch (e) {
        console.log(e)
    }

    const tree = normalizeTree(results.tree || [], account)

    const sponsor = results.sponsor === ZERO_ADDRESS ? results.v1Sponsor : results.sponsor
    const nodeCost = results.nodeCost
    const selfLevel = results.userLevels.selfLevel
    let identity = selfLevel === 0 ? 0 : (selfLevel || identityFromNodeCost(nodeCost))
    console.log('NodeData identity:', identity, ' selfLevel:', selfLevel, ' nodeCost:', nodeCost.toString(), ' sponsor:', sponsor)
    console.log('NodeData teamLeader:', results.teamLeader, ' account:', account)
    // if (sameAddress(account, results.teamLeader._topLeader)) {
    //     identity = 4
    // } else if (sameAddress(account, results.teamLeader._teamLeader)) {
    //     identity = 3
    // }
    const vip = toNumber(results.userLevels.stairLevel) + 1
    const rankRewardedUSDT = WeiToUnits(results.rankTop.score)
    let rankTopPending = WeiToUnits(results.rankTopPending.rankNodeReward.add(results.rankTopPending.rankStairReward))
    rankTopPending = rankTopPending > 3000 ? 3000 : rankTopPending
    const totalClaim = WeiToUnits(results.rankPending.rankNodeReward.add(results.rankPending.rankStairReward))

    const directReward = WeiToUnits(results.nodePending.direct)
    const level2Reward = WeiToUnits(results.nodePending.indirect)
    const teamReward = WeiToUnits(results.nodePending.team)
    const feeReaward = WeiToUnits(results.nodePending.fee)

    const directRewardTotal = WeiToUnits(results.nodeTotal.direct)
    const level2RewardTotal = WeiToUnits(results.nodeTotal.indirect)
    const teamRewardTotal = WeiToUnits(results.nodeTotal.team)
    const feeReawardTotal = WeiToUnits(results.nodeTotal.fee)

    const nodeClaim = WeiToUnits(results.nodePending.direct.add(results.nodePending.indirect).add(results.nodePending.team).add(results.nodePending.fee))
    const totalClaimed = WeiToUnits(results.nodeTotal.direct.add(results.nodeTotal.indirect).add(results.nodeTotal.team).add(results.nodeTotal.fee))

    const totalCostLedger = results.nodeCostLedger.totalValue
        .add(results.usdtCostLedger.totalValue)
        .add(results.cvcdCostLedger.totalValue)
        .add(results.v1TotalInvested)
        .add(results.v1TotalOrderInvested)
        .add(V1_RESERVE_BASE)
    const release = normalizeReleaseList(results.releaseList)
    const claimCVC = WeiToUnits(results.claimCVC)
    return {
        rankRewardedUSDT,
        rankTopPending,
        rankDisqualified: results.rankOf.disqualified && results.rankPending.rankNodeReward.add(results.rankPending.rankStairReward).eq(0),

        totalClaim,
        shareReward: 0,
        sharePercent: 0,

        tree: tree.list,
        nodeClaim,
        level2Reward,
        directReward,
        teamReward,
        feeReaward,
        
        level2RewardTotal,
        directRewardTotal,
        teamRewardTotal,
        feeReawardTotal,

        
        // directPerformance: tree.directPerformance,
        // level2Performance: tree.level2Performance,
        directPerformance: WeiToUnits(results.userLevels.directPerformance),
        level2Performance: WeiToUnits(results.userLevels.indirectPerformance),
        vip,
        identity,
        selfLevel,
        levels: results.levels.maxLevel * 1 + 1,

        rankHeadId: results.rankTopOf.exists ? results.rankTopOf.scoreReachedAt.toString() : '--',
        userRankId: results.rankOf.exists ? results.rankOf.scoreReachedAt.toString() : '--',
        sponsor,
        claimCVC: claimCVC,
        totalInvested: WeiToUnits(totalCostLedger),
        securityTreasuryAddress: results.securityTreasury,
        securityTreasuryUSDT: WeiToUnits(securityTreasuryUSDT),
        rankHeadAddress: results.rankTop.user,
        registed: sponsor !== ZERO_ADDRESS,
        isNode: nodeCost.gt(0),

        usdtInvested: WeiToUnits(nodeCost),
        releaseList: release.releaseList,
        totalReleaseableCVC: release.totalReleaseableCVC,
        releaseCycle: identity === 0 ? 104 : 52,
        releasedCVC: release.releasedCVC,
        totalClaimed
    }
}
NodeData.Type = NODE_TYPE

export function Claim(address) {
    const reward = RewardController()
    return reward.claimRankRewarded(address)
}

export function ClaimNode(address) {
    const reward = RewardController()
    return reward.claimNodeRewarded(address)
}

export function ClaimCVC(address) {
    const reward = RewardController()
    return reward.claimCvc(address)
}

export function BuyNodeV2(amount, sponsor) {
    const reward = RewardController()
    return reward.addNodeCost(amount, sponsor, PROTOCOL_HASH)
}

export async function ApprovesUSDT() {
    return Approves([
        {
            token: USDT_TOKEN,
            isMax: true,
            sender: REWARD_CONTROLLER
        }
    ], {provider: GetGlobalSigner()})
}

export async function ApprovesCVCD() {
    return Approves([
        {
            token: CVCD_TOKEN,
            isMax: true,
            sender: REWARD_CONTROLLER
        }
    ], {provider: GetGlobalSigner()})
}

// CVCD_SWAP_JS_START: CvcdSwap approvals, readers, and write calls.
export async function ApprovesCVCForCvcdSwap() {
    return Approves([
        {
            token: CVC_TOKEN,
            isMax: true,
            sender: CVCD_SWAP
        }
    ], {provider: GetGlobalSigner()})
}

export async function ApprovesCVCDForCvcdSwap() {
    return Approves([
        {
            token: CVCD_TOKEN,
            isMax: true,
            sender: CVCD_SWAP
        }
    ], {provider: GetGlobalSigner()})
}

const CVCD_SWAP_TYPE = {
    exchangeQuota: 0,
    totalStaked: 0,
    totalExchanged: 0,
    lockPeriod: 0,
    usdtReleasePeriod: 0,
    rewardRate: 0,
    rewardDenominator: 1,
    feeRate: 0,
    feeDenominator: 1,
    totalClaimableCVC: 0,
    totalClaimableCVCD: 0,
    totalLockedCVC: 0,
    totalClaimableUSDT: 0,
    orders: [],
    usdtReleaseOrders: []
}

function normalizeCvcdSwapOrders(orders, rewardRate, rewardDenominator, orderMeta = {}) {
    const now = new Date() * 1
    let totalClaimableCVC = BN(0)
    let totalClaimableCVCD = BN(0)
    let totalLockedCVC = BN(0)
    const safeRewardDenominator = rewardDenominator.gt(0) ? rewardDenominator : BN(1)
    const previews = orderMeta.previews || []
    const releasedCVC = orderMeta.releasedCVC || []
    const releasedCVCD = orderMeta.releasedCVCD || []

    const list = orders.map((v, i) => {
        const amount = v.amount || v[0]
        const preview = previews[i]
        const startTime = toNumber(v.startTime || v[1]) * 1000
        const unlockTime = preview ? (preview.unlockTime || preview[2]) : (v.unlockTime || v[2])
        const unlockEnd = toNumber(unlockTime) * 1000
        const cvcClaimable = preview ? (preview.cvcAmount || preview[0]) : BN(0)
        const cvcdClaimable = preview ? (preview.cvcdReward || preview[1]) : BN(0)
        const claimed = preview ? (preview.claimed === undefined ? preview[3] : preview.claimed) : (v.claimed === undefined ? v[3] : v.claimed)
        const claimable = preview ? (preview.claimable === undefined ? preview[4] : preview.claimable) : (!claimed && (cvcClaimable.gt(0) || cvcdClaimable.gt(0)))
        const cvcdReward = amount.mul(rewardRate).div(safeRewardDenominator)
        const progress = unlockEnd <= startTime ? 100 : Math.max(0, Math.min((now - startTime) / (unlockEnd - startTime) * 100, 100))
        const unlockDate = unlockEnd === 0 ? '--' : new Date(unlockEnd).Format('yyyy.MM.dd hh:mm')

        if (claimable) {
            totalClaimableCVC = totalClaimableCVC.add(cvcClaimable)
            totalClaimableCVCD = totalClaimableCVCD.add(cvcdClaimable)
        }
        if (!claimed && !claimable) {
            totalLockedCVC = totalLockedCVC.add(amount)
        }

        return {
            id: i,
            cvcAmount: WeiToUnits(amount),
            cvcdReward: WeiToUnits(cvcdReward),
            cvcClaimable: WeiToUnits(cvcClaimable),
            cvcdClaimable: WeiToUnits(cvcdClaimable),
            releasedCVC: WeiToUnits(releasedCVC[i] || BN(0)),
            releasedCVCD: WeiToUnits(releasedCVCD[i] || BN(0)),
            startTime,
            startDate: startTime === 0 ? '--' : new Date(startTime).Format('yyyy.MM.dd hh:mm'),
            unlockEnd,
            unlockDate,
            endDate: unlockDate,
            unlockRemainTime: unlockEnd - now,
            progress,
            claimed,
            claimable
        }
    })

    list.sort((a, b) => {
        if (a.claimed !== b.claimed) return a.claimed ? 1 : -1
        if (a.claimable !== b.claimable) return a.claimable ? -1 : 1
        return a.unlockRemainTime - b.unlockRemainTime
    })

    return {
        orders: list,
        totalClaimableCVC: WeiToUnits(totalClaimableCVC),
        totalClaimableCVCD: WeiToUnits(totalClaimableCVCD),
        totalLockedCVC: WeiToUnits(totalLockedCVC)
    }
}

function normalizeCvcdSwapUsdtReleaseOrders(orders, previews = []) {
    const now = new Date() * 1
    let totalClaimableUSDT = BN(0)

    const list = orders.map((v, i) => {
        const amount = v.amount || v[0]
        const released = v.released || v[1]
        const startTime = toNumber(v.startTime || v[2]) * 1000
        const endTime = toNumber(v.endTime || v[3]) * 1000
        const preview = previews[i]
        const claimableUSDT = preview ? (preview.usdtAmount || preview[0]) : BN(0)
        const fullyReleased = preview ? (preview.released === undefined ? preview[2] : preview.released) : released.gte(amount)
        const releasable = preview ? (preview.releasable === undefined ? preview[3] : preview.releasable) : claimableUSDT.gt(0)
        const progress = endTime <= startTime ? 100 : Math.max(0, Math.min((now - startTime) / (endTime - startTime) * 100, 100))

        if (releasable) {
            totalClaimableUSDT = totalClaimableUSDT.add(claimableUSDT)
        }

        return {
            id: i,
            usdtAmount: WeiToUnits(amount),
            releasedUSDT: WeiToUnits(released),
            claimableUSDT: WeiToUnits(claimableUSDT),
            startTime,
            startDate: startTime === 0 ? '--' : new Date(startTime).Format('yyyy.MM.dd hh:mm'),
            endTime,
            endDate: endTime === 0 ? '--' : new Date(endTime).Format('yyyy.MM.dd hh:mm'),
            unlockRemainTime: endTime - now,
            progress,
            released: fullyReleased,
            releasable
        }
    })

    return {
        orders: list,
        totalClaimableUSDT: WeiToUnits(totalClaimableUSDT)
    }
}

export async function CvcdSwapData(address) {
    const cvcdSwap = CvcdSwapContract()
    const multiCall = Multi()
    let results

    try {
        const callResults = await multiCall.all({
            exchangeQuota: cvcdSwap.calls.exchangeQuota(address),
            totalStaked: cvcdSwap.calls.totalStaked(address),
            totalExchanged: cvcdSwap.calls.totalExchanged(address),
            lockPeriod: cvcdSwap.calls.LOCK_PERIOD(),
            usdtReleasePeriod: cvcdSwap.calls.USDT_RELEASE_PERIOD(),
            rewardRate: cvcdSwap.calls.CVCD_REWARD_RATE(),
            rewardDenominator: cvcdSwap.calls.CVCD_REWARD_DENOMINATOR(),
            feeRate: cvcdSwap.calls.feeRate(),
            feeDenominator: cvcdSwap.calls.FEE_DENOMINATOR(),
            orders: cvcdSwap.calls.stakeOrders(address),
            usdtReleaseOrders: cvcdSwap.calls.usdtReleaseOrders(address)
        })
        results = callResults
    } catch (e) {
        console.log(e)
        return CVCD_SWAP_TYPE
    }
    console.log(
        "swap results", results
    )
    const orderList = results.orders || []
    const usdtReleaseOrderList = results.usdtReleaseOrders || []
    let orderMeta = {}
    if (orderList.length > 0) {
        try {
            orderMeta = await multiCall.all({
                previews: orderList.map((v, i) => cvcdSwap.calls.previewClaim(address, i)),
                releasedCVC: orderList.map((v, i) => cvcdSwap.calls.releasedCvcOfOrder(address, i)),
                releasedCVCD: orderList.map((v, i) => cvcdSwap.calls.releasedCvcdRewardOfOrder(address, i))
            })
        } catch (e) {
            console.log(e)
            orderMeta = {}
        }
    }
    let usdtReleaseOrderMeta = {}
    if (usdtReleaseOrderList.length > 0) {
        try {
            usdtReleaseOrderMeta = await multiCall.all({
                previews: usdtReleaseOrderList.map((v, i) => cvcdSwap.calls.previewReleaseUSDT(address, i))
            })
        } catch (e) {
            console.log(e)
            usdtReleaseOrderMeta = {}
        }
    }

    const orders = normalizeCvcdSwapOrders(
        orderList,
        results.rewardRate,
        results.rewardDenominator,
        orderMeta
    )
    const usdtReleaseOrders = normalizeCvcdSwapUsdtReleaseOrders(
        usdtReleaseOrderList,
        usdtReleaseOrderMeta.previews || []
    )

    return {
        exchangeQuota: WeiToUnits(results.exchangeQuota),
        totalStaked: WeiToUnits(results.totalStaked),
        totalExchanged: WeiToUnits(results.totalExchanged),
        lockPeriod: toNumber(results.lockPeriod),
        usdtReleasePeriod: toNumber(results.usdtReleasePeriod),
        rewardRate: toNumber(results.rewardRate),
        rewardDenominator: toNumber(results.rewardDenominator) || 1,
        feeRate: toNumber(results.feeRate),
        feeDenominator: toNumber(results.feeDenominator) || 1,
        totalClaimableCVC: orders.totalClaimableCVC,
        totalClaimableCVCD: orders.totalClaimableCVCD,
        totalLockedCVC: orders.totalLockedCVC,
        totalClaimableUSDT: usdtReleaseOrders.totalClaimableUSDT,
        orders: orders.orders,
        usdtReleaseOrders: usdtReleaseOrders.orders
    }
}
CvcdSwapData.Type = CVCD_SWAP_TYPE

export function StakeCvcForCvcdSwap(amount) {
    const cvcdSwap = CvcdSwapContract()
    return cvcdSwap.stake(amount)
}

export function ExchangeCvcdByStakeQuota(amount) {
    const cvcdSwap = CvcdSwapContract()
    return cvcdSwap.exchangeCVCDToUSDT(amount)
}

export function ClaimCvcdSwapOrder(user) {
    const cvcdSwap = CvcdSwapContract()
    return cvcdSwap.claimAll(user)
}

export function ReleaseAllCvcdSwapUSDT(user) {
    const cvcdSwap = CvcdSwapContract()
    return cvcdSwap.releaseAllUSDT(user)
}
// CVCD_SWAP_JS_END: CvcdSwap approvals, readers, and write calls.

export async function ApprovesAll() {
    return Approves([
        {
            token: USDT_TOKEN,
            isMax: true,
            sender: REWARD_CONTROLLER
        },
        {
            token: CVCD_TOKEN,
            isMax: true,
            sender: REWARD_CONTROLLER
        }
    ], {provider: GetGlobalSigner()})
}

const STAIR_TYPE = {
    fee: 0,
    claimStair: 0,
    registed: null,
    sponsor: ZERO_ADDRESS,
    orderNetTnvest: 0,
    totalNewInvest: 0,
    totalOrderRewarded: 0,
    vip: 0,
    totalLocked: 0,
    totalRewarded: 0,
    initialVip: 0,
    initialVipRate: 0,
    maxInvestable: 0,
    vipRate: '--',
    orders: [],
    pendingReward: 0,
    unlockable: 0
}

function toFixeded(num) {
    return num.toFixed(3)
    // return Math.floor(num * 1_000_000_000) / 1_000_000_000
}

function _GetVipRate(vip) {
    let rate = vip === 51 ? 14 : 3.5 + vip * 0.2
    return rate.toFixed(4) * 1
}

function _GetVipRateBps(vip) {
    return Math.round(_GetVipRate(vip) * 10000)
}

function _GetUpgradeNewInvest(order) {
    const nextVipRateBps = _GetVipRateBps(toNumber(order.stairLevel) + 1)
    const upgradeReward = order.initialTnvest
        .mul(99)
        .mul(nextVipRateBps)
        .div(100000000)
    return order.newInvest.add(upgradeReward)
}

function _GetCycle(newVip, intInvest, newInvest) {
    console.log({
        newVip, intInvest, newInvest
    })
    // let k = []
    // let inits = intInvest
    // for(let i = newVip; i < 52; i++) {
    //     inits = inits * (_GetVipRate(i) / 100 + 1)
    //     if (i > newVip) {
    //         inits = inits * 0.993
    //     }
    //     k.push(inits)
    // }
    // console.log(k)
    if (toFixeded((100 + _GetVipRate(newVip)) / 100 * intInvest) === toFixeded(newInvest)) return 0
    let _n = toFixeded(newInvest * 0.99)
    let i;
    for( i = 0; i < 51; i++) {
        _n = toFixeded(_n / 0.99 / (100 + _GetVipRate(newVip - i)) * 100)
        // console.log(
        //     _n,
        //     intInvest,
        //     toFixeded(intInvest)
        // )
        // console.log(
        //     (_n - toFixeded(intInvest)) ** 2 / _n
        // )
        if ((_n - toFixeded(intInvest))**2 / _n < 0.0002) break;
        // if (_n == toFixeded(intInvest)) break;
    }
    // console.log({
    //     i, newVip, intInvest, newInvest
    // })
    return i
    
}

export async function StairData(address) {
    const ma = MAContract()
    const reward = RewardController()
    const multiCall = Multi()
    let results
    try {
        const callResults = await multiCall.all({
            levels: ma.calls.currentVipOf(address),
            orders: ma.calls.getOrder(address),
            sponsor: ma.calls.sponsor(address),
            rewardCaps: reward.calls.getStairRewardCaps(address),
            claimStair: reward.calls.getStairPendingRewarded(address),
            totalInvested: reward.calls.getStairPrincipal(address),
            // investReward: reward.calls.getStairInvestPendingRewarded(address),
            getStairPendingRewarded: reward.calls.getStairPendingRewarded(address),
            fee: reward.calls.getStairFee(address)
        })
        results = {
            levels: callResults.levels,
            orders: callResults.orders,
            sponsor: callResults.sponsor,
            rewardCaps: callResults.rewardCaps,
            claimStair: callResults.claimStair,
            investReward: 0, // callResults.investReward,
            getStairPendingRewarded: callResults.getStairPendingRewarded,
            totalInvested: callResults.totalInvested,
            fee: callResults.fee
        }
        console.log(
            // differenceRewarded
            results.rewardCaps, " resultsresultsresultsresults"
        )
    } catch (e) {
        console.log(e)
        return STAIR_TYPE
    }

    const sponsor = results.sponsor
    const registed = sponsor !== ZERO_ADDRESS
    const now = new Date() * 1
    const day7 = 7 * 24 * 3600 * 1000
    let totalLocked = BN(0)
    let totalPrincipal = BN(0)
    let totalRewarded = BN(0)
    let totalNewInvest = BN(0)

    results.orders.forEach(v => {
        const closed = toNumber(v.lastSettleTime) <= 0
        totalPrincipal = totalPrincipal.add(v.initialTnvest)
        totalNewInvest = totalNewInvest.add(v.newInvest)
        if (!closed) {
            totalLocked = totalLocked.add(v.newInvest)
        }
    })

    const maxDifferenceProfit = (results.rewardCaps.principal)
    const currentStaticProfit = totalNewInvest.gt(totalPrincipal)
        ? totalNewInvest.sub(totalPrincipal)
        : BN(0)
    const currentStaticRemaining = totalPrincipal.gt(currentStaticProfit)
        ? totalPrincipal.sub(currentStaticProfit)
        : BN(0)

    const orders = results.orders.map((v, i) => {
        const invested = WeiToUnits(v.newInvest)
        const initialInvest = WeiToUnits(v.initialTnvest)
        const startTime = toNumber(v.startTime) * 1000
        const reinvestTime = toNumber(v.lastSettleTime) * 1000
        const closed = reinvestTime <= 0

        totalRewarded = totalRewarded.add(v.newInvest)

        const unlockEnd = day7 + reinvestTime
        // console.log('StairData order:', i, ' startTime:', startTime, ' reinvestTime:', reinvestTime, ' unlockEnd:', unlockEnd, ' closed:', closed)
        const unlockRemainTime = unlockEnd - now
        const stairLevel = toNumber(v.stairLevel)
        const vip = stairLevel + 1
        const vipRate = _GetVipRate(stairLevel)
        // const cycle = Math.max(1, stairLevel - initLevel + 1)
        const cycle = _GetCycle(v.stairLevel, WeiToUnits(v.initialTnvest)*1, invested*1) + 1
        const upgradeNewInvest = _GetUpgradeNewInvest(v)
        const upgradeReward = upgradeNewInvest.gt(v.newInvest)
            ? upgradeNewInvest.sub(v.newInvest)
            : BN(0)
        const upgradeExcess = upgradeReward.gt(currentStaticRemaining)
            ? upgradeReward.sub(currentStaticRemaining)
            : BN(0)
        const upgradeLimited = false // !closed && totalPrincipal.gt(0) && upgradeExcess.gt(0)
        // console.log(
        //     totalInitialInvest,
            
        //     upgradeLimited,
        //     "12312"
        // )
        return {
            id: i,
            startTime,
            invested,
            initialInvest,
            startDate: reinvestTime === 0 ? '--' : new Date(reinvestTime).Format('yyyy.MM.dd hh:mm'),
            unlockRemainTime,
            unlockEnd,
            vip,
            vipRate,
            closed,
            upgradeLimited,
            upgradeRemaining: WeiToUnits(currentStaticRemaining),
            upgradeExcess: WeiToUnits(upgradeExcess),
            reinvestTime,
            cycle
        }
    })

    orders.sort((a, b) => {
        if (a.closed !== b.closed) return a.closed ? 1 : -1
        if (a.closed) return b.id - a.id
        return a.unlockRemainTime - b.unlockRemainTime
    })

    const initLevel = toNumber(results.levels.initLevel)
    const maxLevel = toNumber(results.levels.maxLevel)
    const initialVip = initLevel + 1
    const initialVipRate = _GetVipRate(initLevel)
    const orderNetTnvest = WeiToUnits(maxDifferenceProfit)
    const maxStair = BN(30000000000).mul(BN(10).pow(18))
    const maxInvestable = WeiToUnits(maxDifferenceProfit.gte(maxStair) ? BN(0) : maxStair.sub(maxDifferenceProfit))
    

    // console.log(
    //     WeiToUnits(results.rewardCaps.differenceRewarded),
    //     WeiToUnits(results.rewardCaps.differenceRemaining),
    //     WeiToUnits(results.rewardCaps.principal),
        

    // )
    const fee = WeiToUnits(results.fee)
    return {
        registed,
        sponsor,
        orderNetTnvest,
        totalNewInvest: WeiToUnits(totalNewInvest),
        totalOrderRewarded: WeiToUnits(results.rewardCaps.differenceRewarded),
        unlockable: WeiToUnits(results.rewardCaps.differenceRemaining),
        vip: vipFromLevels(results.levels),
        vipRate: _GetVipRate(Math.max(maxLevel, initLevel)),
        initialVip,
        initialVipRate,
        orders,
        totalLocked: WeiToUnits(totalLocked),
        totalPrincipal: WeiToUnits(totalPrincipal),
        staticNetProfit: WeiToUnits(currentStaticProfit),
        totalRewarded: WeiToUnits(totalRewarded.add(results.investReward).add(results.claimStair)),
        maxInvestable,
        claimStair: WeiToUnits(results.claimStair.add(results.fee)),
        pendingReward: WeiToUnits(results.getStairPendingRewarded),
        fee
    }
}
StairData.Type = STAIR_TYPE

export function BuyWithCVCDOrder(amount, sponsor) {
    const reward = RewardController()
    return reward["addStairCost(uint256,uint256,address,bytes32)"](amount, amount, sponsor, PROTOCOL_HASH)
}

export function BuyWithUSDTOrder(amount, sponsor) {
    const reward = RewardController()
    return reward["addStairCost(uint256,uint256,address,bytes32)"](amount, 0, sponsor, PROTOCOL_HASH)
}


export function SetOrder(uid, type) {
    const ma = MAContract()
    if (type === 1) return ma.updateLadderOrder(uid)
    return ma.closeLadderOrder(uid)
}

export function ClaimStair(address) {
    const reward = RewardController()
    return reward.claimStairRewarded(address)
}

export function Swap(amount) {
    const reward = RewardController()
    return reward.exchangeCVCDToUSDT(amount)
}
